import { audio } from '../../../../../../audio/AudioManager.js';
import type { LogicalBounds } from '../../../../../shared/layout/index.js';
import { NumberForgeChallenge } from '../minigames/NumberForgeChallenge.js';
import {
  NUMBER_FORGE_FEEDBACK,
  type NumberForgeChargeResult,
  type NumberForgeSnapshot,
  type NumberForgeSource,
} from '../minigames/NumberForgeState.js';
import {
  decomposeNumberForgeValue,
  type NumberForgeConfig,
  type NumberForgePlace,
} from '../minigames/NumberForgeConfig.js';
import { NUMBER_FORGE_ASSETS } from './NumberForgeAssets.js';

const FONT_UI = '"Nunito", "Segoe UI", Arial, sans-serif';

export interface NumberForgeViewOptions {
  readonly reducedMotion: boolean;
  readonly celebrateCompletion: boolean;
  readonly onComplete: (snapshot: NumberForgeSnapshot) => void;
}

type PendingTransfer = {
  readonly source: NumberForgeSource;
  readonly place: NumberForgePlace;
};

/** Production-art presentation for the rendering-independent Number Forge engine. */
export class NumberForgeView {
  readonly controller: NumberForgeChallenge;

  private readonly root: Phaser.GameObjects.Container;
  private readonly missionPanel: Phaser.GameObjects.Image;
  private readonly machine: Phaser.GameObjects.Image;
  private readonly resultCore: Phaser.GameObjects.Image;
  private readonly resultText: Phaser.GameObjects.Text;
  private readonly sourceAIngot: Phaser.GameObjects.Image;
  private readonly sourceBIngot: Phaser.GameObjects.Image;
  private readonly sourceALabel: Phaser.GameObjects.Text;
  private readonly sourceBLabel: Phaser.GameObjects.Text;
  private readonly tensChannel: Phaser.GameObjects.Image;
  private readonly onesChannel: Phaser.GameObjects.Image;
  private readonly instruction: Phaser.GameObjects.Text;
  private readonly keyboardHelp: Phaser.GameObjects.Text;
  private readonly chargeButton: Phaser.GameObjects.Image;
  private readonly hintButton: Phaser.GameObjects.Image;
  private readonly resetButton: Phaser.GameObjects.Image;
  private readonly sourceAZone: Phaser.GameObjects.Zone;
  private readonly sourceBZone: Phaser.GameObjects.Zone;
  private readonly dynamicObjects: Phaser.GameObjects.GameObject[] = [];
  private safe?: LogicalBounds;
  private compact = false;
  private suspended = false;
  private moving = false;
  private destroyed = false;
  private pendingTransfer?: PendingTransfer;
  private movingImage?: Phaser.GameObjects.Image;
  private transferTrail?: Phaser.GameObjects.Image;
  private feedback?: string;
  private completionVisualPlayed = false;

  constructor(
    private readonly scene: Phaser.Scene,
    readonly config: NumberForgeConfig,
    private readonly options: NumberForgeViewOptions,
  ) {
    this.controller = new NumberForgeChallenge(config, {
      onStateChanged: snapshot => this.renderState(snapshot),
      onFeedback: result => this.showFeedback(result),
      onComplete: (_completedConfig, snapshot) => options.onComplete(snapshot),
    });

    this.root = scene.add.container(0, 0).setDepth(20);
    this.missionPanel = scene.add.image(640, 105, NUMBER_FORGE_ASSETS.missionPanel.key);
    this.machine = scene.add.image(640, 315, NUMBER_FORGE_ASSETS.forgeMachine.key);
    this.resultCore = scene.add.image(640, 460, NUMBER_FORGE_ASSETS.resultCore.key).setAlpha(0.34);
    this.resultText = this.makeText('', 44).setDepth(44);
    this.sourceAIngot = scene.add.image(290, 300, NUMBER_FORGE_ASSETS.sourceIngot.key).setDepth(26);
    this.sourceBIngot = scene.add.image(990, 300, NUMBER_FORGE_ASSETS.sourceIngot.key).setDepth(26);
    this.sourceALabel = this.makeText(`${config.addendA}`, 29).setDepth(48);
    this.sourceBLabel = this.makeText(`${config.addendB}`, 29).setDepth(48);
    this.tensChannel = scene.add.image(455, 510, NUMBER_FORGE_ASSETS.tensChannel.key).setDepth(24);
    this.onesChannel = scene.add.image(825, 510, NUMBER_FORGE_ASSETS.onesChannel.key).setDepth(24);
    this.instruction = this.makeText('Open both number-energy ingots.', 19)
      .setDepth(70).setWordWrapWidth(720).setAlign('center');
    this.keyboardHelp = this.makeText('A/B open • T tens • O ones • Enter charge • H hint • R reset', 13)
      .setDepth(70).setAlpha(0.9);
    this.chargeButton = scene.add.image(640, 665, NUMBER_FORGE_ASSETS.chargeButton.key)
      .setInteractive({ useHandCursor: true }).setDepth(72);
    this.hintButton = scene.add.image(505, 665, NUMBER_FORGE_ASSETS.hintButton.key)
      .setInteractive({ useHandCursor: true }).setDepth(72);
    this.resetButton = scene.add.image(775, 665, NUMBER_FORGE_ASSETS.resetButton.key)
      .setInteractive({ useHandCursor: true }).setDepth(72);
    this.sourceAZone = scene.add.zone(290, 300, 180, 150)
      .setInteractive({ useHandCursor: true }).setDepth(75);
    this.sourceBZone = scene.add.zone(990, 300, 180, 150)
      .setInteractive({ useHandCursor: true }).setDepth(75);

    this.root.add([
      this.missionPanel,
      this.machine,
      this.resultCore,
      this.resultText,
      this.sourceAIngot,
      this.sourceBIngot,
      this.sourceALabel,
      this.sourceBLabel,
      this.tensChannel,
      this.onesChannel,
      this.instruction,
      this.keyboardHelp,
      this.chargeButton,
      this.hintButton,
      this.resetButton,
      this.sourceAZone,
      this.sourceBZone,
    ]);

    this.sourceAZone.on('pointerdown', () => this.revealSource('a'));
    this.sourceBZone.on('pointerdown', () => this.revealSource('b'));
    this.chargeButton.on('pointerdown', this.charge, this);
    this.hintButton.on('pointerdown', this.hint, this);
    this.resetButton.on('pointerdown', this.reset, this);
    scene.input.keyboard?.on('keydown-A', this.revealA, this);
    scene.input.keyboard?.on('keydown-B', this.revealB, this);
    scene.input.keyboard?.on('keydown-T', this.transferTen, this);
    scene.input.keyboard?.on('keydown-O', this.transferOne, this);
    scene.input.keyboard?.on('keydown-ENTER', this.charge, this);
    scene.input.keyboard?.on('keydown-SPACE', this.charge, this);
    scene.input.keyboard?.on('keydown-H', this.hint, this);
    scene.input.keyboard?.on('keydown-R', this.reset, this);
  }

  layout(safe: LogicalBounds, compact: boolean) {
    if (this.destroyed) return;
    this.finishPendingTransfer();
    this.safe = safe;
    this.compact = compact;
    this.controller.layout({ left: safe.x, right: safe.right, top: safe.y, bottom: safe.bottom });

    const centerX = safe.centerX;
    const sourceGap = Math.min(compact ? 650 : 790, safe.width * 0.7);
    const sourceY = safe.centerY - (compact ? 87 : 75);
    const sourceWidth = compact ? 112 : 128;
    const sourceHeight = compact ? 124 : 141;
    const sourceAX = centerX - sourceGap * 0.5;
    const sourceBX = centerX + sourceGap * 0.5;
    const machineY = safe.centerY - (compact ? 90 : 75);
    const machineWidth = compact ? 158 : 184;
    const machineHeight = compact ? 178 : 207;
    const channelGap = Math.min(compact ? 315 : 370, safe.width * 0.34);
    const channelY = safe.centerY + (compact ? 113 : 125);
    const channelWidth = compact ? 205 : 238;
    const channelHeight = compact ? 144 : 167;

    this.missionPanel.setPosition(centerX, safe.y + (compact ? 74 : 86))
      .setDisplaySize(compact ? 250 : 286, compact ? 154 : 176);
    this.instruction.setPosition(centerX, safe.y + (compact ? 158 : 178))
      .setFontSize(compact ? 15 : 18)
      .setWordWrapWidth(Math.min(safe.width * 0.58, 690));
    this.machine.setPosition(centerX, machineY).setDisplaySize(machineWidth, machineHeight);
    this.resultCore.setPosition(centerX, channelY - 2)
      .setDisplaySize(compact ? 92 : 108, compact ? 88 : 103);
    this.resultText.setPosition(centerX, channelY - 3).setFontSize(compact ? 30 : 38);

    this.sourceAIngot.setPosition(sourceAX, sourceY).setDisplaySize(sourceWidth, sourceHeight);
    this.sourceBIngot.setPosition(sourceBX, sourceY).setDisplaySize(sourceWidth, sourceHeight);
    this.sourceAZone.setPosition(sourceAX, sourceY).setSize(compact ? 150 : 180, compact ? 138 : 160);
    this.sourceBZone.setPosition(sourceBX, sourceY).setSize(compact ? 150 : 180, compact ? 138 : 160);
    this.sourceALabel.setPosition(sourceAX, sourceY + (compact ? 85 : 96)).setFontSize(compact ? 20 : 24);
    this.sourceBLabel.setPosition(sourceBX, sourceY + (compact ? 85 : 96)).setFontSize(compact ? 20 : 24);

    this.tensChannel.setPosition(centerX - channelGap * 0.5, channelY)
      .setDisplaySize(channelWidth, channelHeight);
    this.onesChannel.setPosition(centerX + channelGap * 0.5, channelY)
      .setDisplaySize(channelWidth, channelHeight);

    const controlsY = safe.bottom - (compact ? 40 : 46);
    const buttonWidth = compact ? 88 : 103;
    const buttonHeight = compact ? 63 : 73;
    const buttonGap = compact ? 103 : 121;
    this.chargeButton.setPosition(centerX, controlsY).setDisplaySize(buttonWidth, buttonHeight);
    this.hintButton.setPosition(centerX - buttonGap, controlsY).setDisplaySize(buttonWidth, buttonHeight);
    this.resetButton.setPosition(centerX + buttonGap, controlsY).setDisplaySize(buttonWidth, buttonHeight);
    this.keyboardHelp.setPosition(centerX, controlsY - (compact ? 50 : 58)).setFontSize(compact ? 11 : 13);

    this.renderState(this.controller.snapshot);
  }

  setSuspended(suspended: boolean) {
    this.suspended = suspended;
    this.controller.setSuspended(suspended);
  }

  destroy() {
    if (this.destroyed) return;
    this.destroyed = true;
    this.scene.input.keyboard?.off('keydown-A', this.revealA, this);
    this.scene.input.keyboard?.off('keydown-B', this.revealB, this);
    this.scene.input.keyboard?.off('keydown-T', this.transferTen, this);
    this.scene.input.keyboard?.off('keydown-O', this.transferOne, this);
    this.scene.input.keyboard?.off('keydown-ENTER', this.charge, this);
    this.scene.input.keyboard?.off('keydown-SPACE', this.charge, this);
    this.scene.input.keyboard?.off('keydown-H', this.hint, this);
    this.scene.input.keyboard?.off('keydown-R', this.reset, this);
    this.finishPendingTransfer();
    this.clearDynamicObjects();
    this.root.destroy(true);
  }

  private readonly revealA = () => this.revealSource('a');
  private readonly revealB = () => this.revealSource('b');
  private readonly transferTen = () => this.transferNext('tens');
  private readonly transferOne = () => this.transferNext('ones');

  private revealSource(source: NumberForgeSource) {
    if (this.destroyed || this.suspended || this.moving) return;
    this.feedback = undefined;
    const result = this.controller.dispatch({ type: 'reveal-source', source });
    if (result === 'accepted') audio.unlock();
  }

  private transferNext(place: 'tens' | 'ones') {
    const snapshot = this.controller.snapshot;
    const source = (['a', 'b'] as const).find(candidate => (
      snapshot.revealedSources.includes(candidate)
      && (candidate === 'a' ? snapshot.sourceA[place] : snapshot.sourceB[place]) > 0
    ));
    if (source) this.transfer(source, place);
    else if (!snapshot.revealedSources.includes('a')) this.revealSource('a');
    else if (!snapshot.revealedSources.includes('b')) this.revealSource('b');
  }

  private transfer(source: NumberForgeSource, place: 'tens' | 'ones') {
    if (this.destroyed || this.suspended || this.moving || !this.safe) return;
    const sourceImage = this.getSourcePieceImage(source, place);
    if (!sourceImage) return;

    this.feedback = undefined;
    audio.click();
    if (this.options.reducedMotion) {
      this.controller.dispatch({ type: 'move', source, place });
      return;
    }

    const destination = this.getChannelPiecePosition(place, this.controller.snapshot.channels[place]);
    this.moving = true;
    this.pendingTransfer = { source, place };
    this.movingImage = sourceImage.disableInteractive().setDepth(90);
    const deltaX = destination.x - sourceImage.x;
    const deltaY = destination.y - sourceImage.y;
    this.transferTrail = this.scene.add.image(
      sourceImage.x + deltaX * 0.5,
      sourceImage.y + deltaY * 0.5,
      NUMBER_FORGE_ASSETS.transferTrail.key,
    ).setCrop(18, 18, 438, 234).setDisplaySize(Math.max(72, Math.hypot(deltaX, deltaY) * 0.55), 56)
      .setRotation(Math.atan2(deltaY, deltaX)).setAlpha(0.68).setBlendMode(Phaser.BlendModes.ADD)
      .setDepth(84);
    this.root.add(this.transferTrail);

    this.scene.tweens.add({
      targets: sourceImage,
      x: destination.x,
      y: destination.y,
      scaleX: sourceImage.scaleX * 1.08,
      scaleY: sourceImage.scaleY * 1.08,
      duration: 360,
      ease: 'Cubic.easeInOut',
      onComplete: () => this.finishPendingTransfer(),
    });
    this.scene.tweens.add({
      targets: this.transferTrail,
      alpha: 0,
      duration: 360,
      ease: 'Sine.easeIn',
    });
  }

  private finishPendingTransfer() {
    if (!this.pendingTransfer) return;
    const transfer = this.pendingTransfer;
    this.pendingTransfer = undefined;
    if (this.movingImage) this.scene.tweens.killTweensOf(this.movingImage);
    if (this.transferTrail) {
      this.scene.tweens.killTweensOf(this.transferTrail);
      this.transferTrail.destroy();
    }
    this.movingImage = undefined;
    this.transferTrail = undefined;
    this.moving = false;
    if (!this.destroyed) {
      audio.sparkle();
      this.controller.dispatch({ type: 'move', ...transfer });
    }
  }

  private readonly charge = () => {
    if (this.destroyed || this.suspended || this.moving) return;
    this.feedback = undefined;
    audio.click();
    this.controller.dispatch({ type: 'charge' });
  };

  private readonly hint = () => {
    if (this.destroyed || this.suspended || this.moving) return;
    this.feedback = undefined;
    audio.hint();
    this.controller.dispatch({ type: 'hint' });
  };

  private readonly reset = () => {
    if (this.destroyed || this.suspended || this.moving || this.controller.snapshot.isComplete) return;
    this.feedback = undefined;
    audio.click();
    this.controller.dispatch({ type: 'reset' });
  };

  private showFeedback(result: NumberForgeChargeResult) {
    if (result === 'complete') return;
    this.feedback = NUMBER_FORGE_FEEDBACK[result];
    audio.hint();
    this.renderState(this.controller.snapshot);
  }

  private renderState(snapshot: NumberForgeSnapshot) {
    if (this.destroyed || !this.safe) return;
    this.clearDynamicObjects();
    const aRevealed = snapshot.revealedSources.includes('a');
    const bRevealed = snapshot.revealedSources.includes('b');
    this.sourceAIngot.setAlpha(aRevealed ? 0.32 : 1);
    this.sourceBIngot.setAlpha(bRevealed ? 0.32 : 1);
    this.sourceALabel.setText(aRevealed
      ? this.formatPlaceCounts(snapshot.sourceA.tens, snapshot.sourceA.ones)
      : `${this.config.addendA}`);
    this.sourceBLabel.setText(bRevealed
      ? this.formatPlaceCounts(snapshot.sourceB.tens, snapshot.sourceB.ones)
      : `${this.config.addendB}`);

    if (aRevealed) this.createSourcePieces('a', snapshot);
    if (bRevealed) this.createSourcePieces('b', snapshot);
    this.createChannelPieces('tens', snapshot.channels.tens);
    this.createChannelPieces('ones', snapshot.channels.ones);

    this.machine.setTexture(snapshot.isComplete
      ? NUMBER_FORGE_ASSETS.forgeMachinePowered.key
      : NUMBER_FORGE_ASSETS.forgeMachine.key);
    this.resultCore.setAlpha(snapshot.phase === 'ready-to-charge' ? 0.82 : snapshot.isComplete ? 1 : 0.34);
    this.resultText.setText(snapshot.isComplete ? `${snapshot.target}` : snapshot.phase === 'ready-to-charge' ? '?' : '');
    if (snapshot.isComplete && !this.completionVisualPlayed) {
      this.completionVisualPlayed = true;
      if (this.options.celebrateCompletion) this.playCombineBurst();
    }

    const phaseInstruction = snapshot.phase === 'discovering'
      ? 'Open both number-energy ingots. What place-value pieces are inside?'
      : snapshot.phase === 'sorting'
        ? 'Move every ten to TENS and every one to ONES.'
        : snapshot.phase === 'regrouping'
          ? 'Trade 10 pieces for 1 piece in the next place.'
          : snapshot.phase === 'ready-to-charge'
            ? `${snapshot.channels.tens} tens + ${snapshot.channels.ones} ones. Charge the forge!`
            : `${snapshot.channels.tens} tens + ${snapshot.channels.ones} ones = ${snapshot.target}. Forge powered!`;
    this.instruction.setText(this.feedback ?? snapshot.hint?.message ?? phaseInstruction);

    const interactive = !snapshot.isComplete;
    this.chargeButton.setVisible(interactive);
    this.hintButton.setVisible(interactive);
    this.resetButton.setVisible(interactive);
    this.keyboardHelp.setVisible(interactive);
    if (interactive) {
      this.chargeButton.setInteractive({ useHandCursor: true });
      this.hintButton.setInteractive({ useHandCursor: true });
      this.resetButton.setInteractive({ useHandCursor: true });
      if (!aRevealed) this.sourceAZone.setInteractive({ useHandCursor: true });
      else this.sourceAZone.disableInteractive();
      if (!bRevealed) this.sourceBZone.setInteractive({ useHandCursor: true });
      else this.sourceBZone.disableInteractive();
    } else {
      this.chargeButton.disableInteractive();
      this.hintButton.disableInteractive();
      this.resetButton.disableInteractive();
      this.sourceAZone.disableInteractive();
      this.sourceBZone.disableInteractive();
    }
  }

  private createSourcePieces(source: NumberForgeSource, snapshot: NumberForgeSnapshot) {
    const counts = source === 'a' ? snapshot.sourceA : snapshot.sourceB;
    for (let index = 0; index < counts.tens; index += 1) this.createSourcePiece(source, 'tens', index);
    for (let index = 0; index < counts.ones; index += 1) this.createSourcePiece(source, 'ones', index);
  }

  private createSourcePiece(source: NumberForgeSource, place: 'tens' | 'ones', index: number) {
    const position = this.getSourcePiecePosition(source, place, index);
    const image = this.scene.add.image(
      position.x,
      position.y,
      place === 'tens' ? NUMBER_FORGE_ASSETS.tensPiece.key : NUMBER_FORGE_ASSETS.onesPiece.key,
    ).setDisplaySize(
      place === 'tens' ? (this.compact ? 42 : 48) : (this.compact ? 29 : 33),
      place === 'tens' ? (this.compact ? 43 : 49) : (this.compact ? 31 : 36),
    ).setDepth(54);
    const zone = this.scene.add.zone(position.x, position.y, 64, 64)
      .setInteractive({ useHandCursor: true }).setDepth(76);
    zone.on('pointerdown', () => this.transfer(source, place));
    image.setData({ source, place, sourcePiece: true });
    this.dynamicObjects.push(image, zone);
    this.root.add([image, zone]);
  }

  private createChannelPieces(place: 'tens' | 'ones', count: number) {
    for (let index = 0; index < count; index += 1) {
      const position = this.getChannelPiecePosition(place, index);
      const image = this.scene.add.image(
        position.x,
        position.y,
        place === 'tens' ? NUMBER_FORGE_ASSETS.tensPiece.key : NUMBER_FORGE_ASSETS.onesPiece.key,
      ).setDisplaySize(
        place === 'tens' ? (this.compact ? 34 : 39) : (this.compact ? 24 : 27),
        place === 'tens' ? (this.compact ? 35 : 40) : (this.compact ? 26 : 29),
      ).setDepth(55);
      this.dynamicObjects.push(image);
      this.root.add(image);
    }
  }

  private getSourcePieceImage(source: NumberForgeSource, place: NumberForgePlace) {
    return this.dynamicObjects.find(object => (
      object instanceof Phaser.GameObjects.Image
      && object.getData('sourcePiece') === true
      && object.getData('source') === source
      && object.getData('place') === place
    )) as Phaser.GameObjects.Image | undefined;
  }

  private getSourcePiecePosition(source: NumberForgeSource, place: 'tens' | 'ones', index: number) {
    const safe = this.safe!;
    const sourceGap = Math.min(this.compact ? 650 : 790, safe.width * 0.7);
    const centerX = safe.centerX + (source === 'a' ? -sourceGap * 0.5 : sourceGap * 0.5);
    const sourceY = safe.centerY - (this.compact ? 87 : 75);
    if (place === 'tens') {
      const gap = this.compact ? 39 : 45;
      return { x: centerX + (index - 1) * gap, y: sourceY - (this.compact ? 57 : 65) };
    }
    const column = index % 3;
    const row = Math.floor(index / 3);
    const gapX = this.compact ? 31 : 36;
    const gapY = this.compact ? 32 : 37;
    return { x: centerX + (column - 1) * gapX, y: sourceY + 2 + row * gapY };
  }

  private getChannelPiecePosition(place: 'tens' | 'ones', index: number) {
    const safe = this.safe!;
    const channelGap = Math.min(this.compact ? 315 : 370, safe.width * 0.34);
    const centerX = safe.centerX + (place === 'tens' ? -channelGap * 0.5 : channelGap * 0.5);
    const channelY = safe.centerY + (this.compact ? 113 : 125);
    const columns = 5;
    const column = index % columns;
    const row = Math.floor(index / columns);
    const gapX = place === 'tens' ? (this.compact ? 35 : 40) : (this.compact ? 28 : 32);
    const gapY = place === 'tens' ? (this.compact ? 35 : 40) : (this.compact ? 29 : 33);
    return {
      x: centerX + (column - 2) * gapX,
      y: channelY - (this.compact ? 14 : 16) + row * gapY,
    };
  }

  private clearDynamicObjects() {
    this.dynamicObjects.splice(0).forEach(object => object.destroy());
  }

  private formatPlaceCounts(tens: number, ones: number) {
    return `${tens} ${tens === 1 ? 'ten' : 'tens'} • ${ones} ${ones === 1 ? 'one' : 'ones'}`;
  }

  private playCombineBurst() {
    const burst = this.scene.add.image(
      this.resultCore.x,
      this.resultCore.y,
      NUMBER_FORGE_ASSETS.combineBurst.key,
    ).setCrop(16, 16, 445, 238).setDisplaySize(220, 118)
      .setBlendMode(Phaser.BlendModes.ADD).setDepth(92);
    this.root.add(burst);
    if (this.options.reducedMotion) {
      burst.setAlpha(0.7);
      this.scene.time.delayedCall(180, () => burst.destroy());
      return;
    }
    burst.setAlpha(0).setScale(0.7);
    this.scene.tweens.add({
      targets: burst,
      alpha: { from: 0, to: 0.9 },
      scaleX: 1.15,
      scaleY: 1.15,
      duration: 420,
      yoyo: true,
      hold: 80,
      ease: 'Sine.easeOut',
      onComplete: () => burst.destroy(),
    });
  }

  private makeText(text: string, fontSize: number) {
    return this.scene.add.text(0, 0, text, {
      fontFamily: FONT_UI,
      fontSize: `${fontSize}px`,
      fontStyle: 'bold',
      align: 'center',
      color: '#fff5cc',
      stroke: '#251327',
      strokeThickness: 6,
      shadow: { offsetX: 0, offsetY: 2, blur: 4, color: '#100713', fill: true },
    }).setOrigin(0.5);
  }
}
